import { useContext, useState } from 'react';
import { PDFDocument } from 'pdf-lib';

import { FileContext } from '../context/FileContext';
import { setEditorOnboarded } from '../services/user';
import { useMutation, useQuery } from 'react-query';
import {
  removeImageFromS3Bucket,
  addFormFieldsForGenerate,
  generateRealtimeFilePreviewForGenerateFlow,
} from '../services/file';

export function useGenerate() {
  const [isLoading, setLoading] = useState(false);
  const [isPreview, setIsPreview] = useState(false);
  const [showSavedDialog, setShowSavedDialog] = useState(false);
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);

  const {
    itemId,
    fileId,
    documentRef,
    pdfDimensions,
    setPreviewFile,
    addedFormFields,
    generateFlowData,
    updateFormFields,
    uploadUpdatedPDF,
    pagesAddedByUser,
    setPageDeleteIndex,
    setGenerateFlowData,
    imagesToRemoveArray,
    setPagesAddedByUser,
    currentSelectedItem,
    setShowLineItemModal,
    setDisplayOnboardingFlow,
    setAddedFormFieldsChanged,
  } = useContext(FileContext);

  const activeFile = generateFlowData?.pdfFileData?.updated_file;
  const activeFileType = generateFlowData?.pdfFileData?.file_type;
  const isWordFile = ['docx', 'doc'].includes(activeFileType);

  const {
    refetch: updatePreviewFileForGenerateFlow,
    isLoading: isPreviewLoading,
  } = useQuery(
    ['generate-realtime-preview-file', itemId, fileId],
    () =>
      generateRealtimeFilePreviewForGenerateFlow(
        fileId,
        itemId,
        addedFormFields,
      ),
    {
      enabled: false,
      onSuccess: (res) => {
        if (addedFormFields[currentSelectedItem.current]) {
          addedFormFields[currentSelectedItem.current].isActive = false;
          updateFormFields([...addedFormFields]);
        }
        currentSelectedItem.current = null;
        setPreviewFile(res?.data?.file);
      },
      refetchOnWindowFocus: false,
    },
  );

  const { mutate: saveFormFields } = useMutation(addFormFieldsForGenerate);

  const updateGenerateFlowData = (key, val) => {
    setGenerateFlowData((prev) => {
      return {
        ...prev,
        [key]: val,
      };
    });
  };

  const updatePdfFileData = (val) => {
    updateGenerateFlowData('pdfFileData', val);
  };

  const addPageInPdfFileData = async (pageIndex) => {
    try {
      const pageSize = [
        pdfDimensions?.width / pdfDimensions?.scale,
        pdfDimensions?.height / pdfDimensions?.scale,
      ];
      const pdfDoc = await PDFDocument.load(activeFile);

      pdfDoc.insertPage(pageIndex, pageSize);
      const base64DataUri = await pdfDoc.saveAsBase64({ dataUri: true });

      let updatedPdfFileData = generateFlowData?.pdfFileData;
      updatedPdfFileData = {
        ...updatedPdfFileData,
        updated_file: base64DataUri,
      };
      updatePdfFileData(updatedPdfFileData);

      setPagesAddedByUser((prevPageIndexes) => {
        const updatedIndexes = [];
        prevPageIndexes?.forEach((x) => {
          if (x >= pageIndex) {
            updatedIndexes.push(x + 1);
          } else {
            updatedIndexes.push(x);
          }
        });
        updatedIndexes.push(pageIndex);
        return updatedIndexes;
      });
      const updatedFields = addedFormFields?.map((field) => {
        if (field.formField.pageIndex >= pageIndex) {
          return {
            ...field,
            formField: {
              ...field.formField,
              pageIndex: field.formField.pageIndex + 1,
              pageNumber: field.formField.pageNumber + 1,
            },
          };
        } else {
          return field;
        }
      });
      updateFormFields([...updatedFields]);
    } catch (error) {
      console.error('Error adding page to PDF:', error);
    }
  };

  const removePageFromPdfFileData = async (pageIndex) => {
    try {
      const pdfDoc = await PDFDocument.load(activeFile);
      if (pageIndex >= 0 && pageIndex < pdfDoc.getPageCount()) {
        pdfDoc.removePage(pageIndex);
        const base64DataUri = await pdfDoc.saveAsBase64({ dataUri: true });

        let updatedPdfFileData = generateFlowData?.pdfFileData;
        updatedPdfFileData = {
          ...updatedPdfFileData,
          updated_file: base64DataUri,
        };
        updatePdfFileData(updatedPdfFileData);

        setPagesAddedByUser((prev) => {
          const updatedIndexes = [];
          prev?.forEach((x) => {
            if (x > pageIndex) {
              updatedIndexes.push(x - 1);
            } else if (x < pageIndex) {
              updatedIndexes.push(x);
            }
          });
          return updatedIndexes;
        });
        const updatedFields = addedFormFields
          ?.filter((field) => field.formField.pageIndex !== pageIndex)
          ?.map((field) => {
            if (field.formField.pageIndex > pageIndex) {
              return {
                ...field,
                formField: {
                  ...field.formField,
                  pageIndex: field.formField.pageIndex - 1,
                  pageNumber: field.formField.pageNumber - 1,
                },
              };
            } else {
              return field;
            }
          });
        updateFormFields([...updatedFields]);
        setPageDeleteIndex(false);

        //TODO: send the pageDeleteIndex and store in BE.
      } else {
        console.error('Invalid page index:', pageIndex);
      }
    } catch (error) {
      console.error('Error removing page from PDF:', error);
    }
  };

  const updatePdfInBE = ({
    isTemporaryFile = false,
    isUserSavingFields = false,
  }) => {
    const data = generateFlowData?.pdfFileData;
    if (
      (data.filetype !== 'docx' || data.filetype !== 'doc') &&
      data?.original_file !== data?.updated_file
    ) {
      uploadUpdatedPDF(
        {
          file: data?.updated_file,
          fileData: data,
          isTemporaryFile,
          fileHasBeenUpdated: data?.original_file !== data?.updated_file,
        },
        {
          onSuccess: () => {
            if (isTemporaryFile) {
              !isUserSavingFields && updatePreviewFileForGenerateFlow();
            }
          },
        },
      );
    } else {
      !isUserSavingFields && updatePreviewFileForGenerateFlow();
    }
  };

  const handlePreview = async () => {
    const scrollPosition = documentRef?.current?.scrollTop;
    // !isWordFile &&
    await updatePdfInBE({
      isTemporaryFile: true,
    });
    setIsPreview(true);
    setShowLineItemModal(false);
    setTimeout(() => {
      scrollPosition &&
        documentRef.current?.scroll({
          top: scrollPosition,
          behavior: 'smooth',
        });
    }, 300);
  };

  const handleEdit = () => {
    setIsPreview(false);
    const scrollPosition = documentRef?.current?.scrollTop;
    setTimeout(() => {
      scrollPosition &&
        documentRef.current?.scroll({
          top: scrollPosition,
          behavior: 'smooth',
        });
    }, 300);
  };

  const handleCloseOnboardingModal = () => {
    setDisplayOnboardingFlow(false);
    setEditorOnboarded();
  };

  const closeCompleteDialog = () => {
    setShowCompleteDialog(false);
  };

  const handleSave = () => {
    setLoading(true);
    if (addedFormFields.length)
      addedFormFields.forEach((item) => delete item?.isActive);

    saveFormFields(
      {
        id: fileId || localStorage.getItem('fileId'),
        fields: addedFormFields,
        signers_settings: {
          isSigningOrderRequired: false,
          signers: [],
        },
        item_id: itemId,
        pages_added_by_users: pagesAddedByUser,
      },
      {
        onSuccess: (res) => {
          if (res) {
            setShowSavedDialog(true);
            setAddedFormFieldsChanged(false);
            // Update the original pdf in BE
            updatePdfInBE({ fileId: fileId, isUserSavingFields: true });

            imagesToRemoveArray.forEach((imageId) => {
              removeImageFromS3Bucket(imageId);
            });
            setLoading(false);
          }
        },
        onError: () => {
          setLoading(false);
        },
      },
    );
  };

  return {
    isLoading,
    isPreview,
    activeFile,
    activeFileType,
    showSavedDialog,
    isPreviewLoading,
    showCompleteDialog,
    handleEdit,
    handleSave,
    handlePreview,
    updatePdfFileData,
    setShowSavedDialog,
    closeCompleteDialog,
    addPageInPdfFileData,
    removePageFromPdfFileData,
    handleCloseOnboardingModal,
  };
}
